import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Eye, Users, Download, FileText, ExternalLink, BarChart3, Globe2, Sparkles,
  Send, Loader2, Mail, Calendar,
} from "lucide-react";
import { adminFetchAnalytics, adminFetchDigestPreview, adminSendDigest, adminFetchDigestLast, isAdminAuthed, CATEGORY_COLOR } from "@/lib/blogApi";

const fmtPct = (n) => `${Math.round((n || 0) * 100)}%`;
const fmtAgo = (iso) => {
  if (!iso) return "never";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const d = Math.floor(diff / 86400000);
    if (d === 0) return "today";
    if (d === 1) return "1 day ago";
    if (d < 7) return `${d} days ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return iso; }
};

const AdminAnalytics = () => {
  const authed = isAdminAuthed();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("views");
  const [sortDir, setSortDir] = useState("desc");
  const [lastDigest, setLastDigest] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [periodDays, setPeriodDays] = useState(7);

  useEffect(() => {
    if (!authed) return;
    let alive = true;
    setLoading(true);
    Promise.all([adminFetchAnalytics(), adminFetchDigestLast().catch(() => null)])
      .then(([analytics, last]) => {
        if (!alive) return;
        setData(analytics);
        setLastDigest(last);
      })
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [authed]);

  const rows = useMemo(() => {
    const list = [...(data?.posts || [])];
    list.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      const cmp = typeof va === "number" && typeof vb === "number"
        ? va - vb
        : (va ?? "").toString().localeCompare((vb ?? "").toString());
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [data, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const maxViews = useMemo(() => Math.max(1, ...rows.map((r) => r.views || 0)), [rows]);
  const maxRef = useMemo(() => Math.max(1, ...(data?.top_referrers || []).map((r) => r.count)), [data]);

  if (!authed) return <Navigate to="/admin" replace />;

  const totals = data?.totals || { views: 0, unique: 0, leads: 0, pdf_downloads: 0 };

  const openPreview = async () => {
    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewHtml("");
    try {
      const r = await adminFetchDigestPreview(periodDays);
      setPreviewHtml(r.html || "");
    } catch { toast.error("Could not load preview"); }
    finally { setPreviewLoading(false); }
  };
  const sendDigestNow = async () => {
    setDigestBusy(true);
    try {
      const r = await adminSendDigest(periodDays);
      toast.success(`Digest sent to ${r.sent_to}. ${r.stats.inquiries_count} inquiries · ${r.stats.views} views in window.`);
      const last = await adminFetchDigestLast().catch(() => null);
      setLastDigest(last);
      setShowPreview(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Send failed";
      toast.error(msg);
    } finally { setDigestBusy(false); }
  };

  return (
    <div className="pt-[72px] bg-brand-cream/40 min-h-screen" data-testid="page-admin-analytics">
      <div className="container-wide py-10">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-brand-black/65 hover:text-brand-teal mb-6">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>

        <div className="mb-8">
          <p className="eyebrow mb-2">◦  Blog Analytics</p>
          <h1 className="font-display text-4xl text-brand-black">Content Performance</h1>
          <p className="text-sm text-brand-black/60 mt-2 max-w-xl">
            Views, unique visitors, reading completion, and which articles actually convert to PDF downloads + B2B inquiries.
          </p>
        </div>

        {/* Spiruva Insider — weekly digest panel */}
        <div
          data-testid="digest-panel"
          className="bg-brand-black text-brand-cream border border-brand-gold/30 mb-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 grain opacity-15 mix-blend-overlay pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-brand-teal via-brand-gold to-brand-green" />
          <div className="relative p-6 md:p-7 grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
            <div className="md:col-span-7">
              <p className="eyebrow-light mb-2 flex items-center gap-2"><Mail size={12} /> Spiruva Insider</p>
              <h2 className="font-display text-2xl md:text-3xl leading-snug">
                Get the weekly pulse delivered, <span className="italic text-brand-gold">no login required.</span>
              </h2>
              <p className="text-sm text-brand-cream/70 mt-3 leading-relaxed">
                Auto-sends every <strong className="text-brand-cream">Monday 09:00 UTC</strong> with new inquiries, top articles, leads, referrers, and any stale follow-ups.
                You can also send a one-off right now.
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-cream/55 mt-3 inline-flex items-center gap-2">
                <Calendar size={11} /> Last sent: <span className="text-brand-gold">{fmtAgo(lastDigest?.sent_at)}</span>
                {lastDigest?.trigger && (
                  <span className="text-brand-cream/40">· via {lastDigest.trigger}</span>
                )}
              </p>
            </div>
            <div className="md:col-span-5 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-xs text-brand-cream/70">
                <span className="font-mono uppercase tracking-widest text-[10px]">Window</span>
                <select
                  value={periodDays}
                  onChange={(e) => setPeriodDays(Number(e.target.value))}
                  data-testid="digest-period"
                  className="flex-1 bg-brand-black border border-brand-cream/20 text-brand-cream px-3 py-2 text-xs focus:outline-none focus:border-brand-gold"
                >
                  <option value={1}>Last 24 hours</option>
                  <option value={7}>Last 7 days (weekly)</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={openPreview}
                  disabled={previewLoading || digestBusy}
                  data-testid="digest-preview"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 text-xs font-medium uppercase tracking-[0.18em] border border-brand-cream/25 text-brand-cream hover:border-brand-gold hover:text-brand-gold transition disabled:opacity-50"
                >
                  {previewLoading ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                  Preview
                </button>
                <button
                  onClick={sendDigestNow}
                  disabled={digestBusy}
                  data-testid="digest-send"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 text-xs font-medium uppercase tracking-[0.18em] bg-brand-gold text-brand-black hover:bg-brand-cream transition disabled:opacity-60"
                >
                  {digestBusy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  Send Now
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-green/15 border border-brand-green/15 mb-10">
          {[
            { icon: Eye, l: "Total Views", v: totals.views, sub: "All published articles" },
            { icon: Users, l: "Unique Visitors", v: totals.unique, sub: "De-duplicated per day" },
            { icon: Download, l: "PDF Downloads", v: totals.pdf_downloads, sub: "Lead-gated" },
            { icon: Sparkles, l: "Leads Captured", v: totals.leads, sub: "B2B emails collected" },
          ].map((s) => (
            <div key={s.l} data-testid={`analytics-total-${s.l.toLowerCase().replace(/\s+/g, '-')}`} className="bg-white p-6">
              <s.icon size={14} className="text-brand-teal" />
              <p className="font-display text-4xl text-brand-black mt-3">{s.v}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50 mt-1">{s.l}</p>
              <p className="text-[11px] text-brand-black/55 mt-2">{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Top referrers band */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <p className="eyebrow inline-flex items-center gap-2"><Globe2 size={13} /> Top Referrers</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45">Where buyers come from</p>
          </div>
          <div className="bg-white border border-brand-green/15 p-6" data-testid="analytics-referrers">
            {loading ? (
              <p className="text-brand-black/60 text-sm">Loading…</p>
            ) : (data?.top_referrers || []).length === 0 ? (
              <p className="text-brand-black/55 text-sm">No referrer data yet. Visitors with non-direct traffic will appear here.</p>
            ) : (
              <ul className="space-y-3">
                {data.top_referrers.map((r) => (
                  <li key={r.referrer} className="flex items-center gap-4">
                    <span className="font-mono text-xs text-brand-black/85 w-40 truncate">{r.referrer}</span>
                    <div className="flex-1 relative h-2 bg-brand-cream rounded-sm overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-teal to-brand-green transition-all duration-700"
                        style={{ width: `${(r.count / maxRef) * 100}%` }}
                      />
                    </div>
                    <span className="font-mono text-xs text-brand-black/65 w-12 text-right">{r.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Per-post table */}
        <div className="mb-3 flex items-center justify-between">
          <p className="eyebrow inline-flex items-center gap-2"><BarChart3 size={13} /> Per-Article Performance</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45">Click headers to sort</p>
        </div>
        <div className="bg-white border border-brand-green/15 overflow-x-auto">
          <table className="clinical-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort("title")} className="cursor-pointer">Article</th>
                <th>Category</th>
                <th onClick={() => toggleSort("views")} className="cursor-pointer text-right">Views</th>
                <th onClick={() => toggleSort("unique_visitors")} className="cursor-pointer text-right">Unique</th>
                <th onClick={() => toggleSort("avg_completion")} className="cursor-pointer text-right">Avg Read</th>
                <th onClick={() => toggleSort("leads")} className="cursor-pointer text-right">Leads</th>
                <th onClick={() => toggleSort("pdf_downloads")} className="cursor-pointer text-right">PDFs</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center text-brand-black/50" data-testid="analytics-loading">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-brand-black/50 py-10" data-testid="analytics-empty">No data yet.</td></tr>
              ) : rows.map((r) => {
                const color = CATEGORY_COLOR[r.category]?.hex || "#1a4d2e";
                const viewBarPct = ((r.views || 0) / maxViews) * 100;
                return (
                  <tr key={r.id} data-testid={`analytics-row-${r.id}`}>
                    <td className="max-w-[340px]">
                      <p className="font-medium text-brand-black text-sm line-clamp-2">{r.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40">/blog/{r.slug}</p>
                        {!r.isPublished && (
                          <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-brand-gold/15 text-[#8a6e1f] border border-brand-gold/40">Draft</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border" style={{ color, borderColor: `${color}40`, backgroundColor: `${color}10` }}>
                        {r.category}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="relative w-16 h-1.5 bg-brand-cream/60 rounded-sm overflow-hidden hidden md:block">
                          <div className="absolute inset-y-0 left-0 bg-brand-teal transition-all duration-500" style={{ width: `${viewBarPct}%` }} />
                        </div>
                        <span className="font-mono text-sm text-brand-black w-10 text-right">{r.views || 0}</span>
                      </div>
                    </td>
                    <td className="text-right">
                      <span className="font-mono text-sm text-brand-black/85">{r.unique_visitors || 0}</span>
                    </td>
                    <td className="text-right">
                      <span className="font-mono text-sm text-brand-black/85">{r.completion_sessions > 0 ? fmtPct(r.avg_completion) : "—"}</span>
                    </td>
                    <td className="text-right">
                      <span className={`font-mono text-sm ${r.leads > 0 ? "text-brand-green font-medium" : "text-brand-black/40"}`}>
                        {r.leads || 0}
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={`font-mono text-sm ${r.pdf_downloads > 0 ? "text-brand-gold font-medium" : "text-brand-black/40"}`}>
                        {r.pdf_downloads || 0}
                      </span>
                    </td>
                    <td className="text-right">
                      <a
                        href={`/blog/${r.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in new tab"
                        className="inline-flex items-center text-brand-black/40 hover:text-brand-teal transition"
                      >
                        <ExternalLink size={14} />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-brand-black/45 mt-8 font-mono leading-relaxed max-w-3xl">
          <FileText size={11} className="inline mr-1 -mt-0.5" />
          Views are de-duped per visitor per day. Completion % is measured client-side based on scroll position (read-progress beacon).
          PDF downloads = leads captured via the article gate. All tracking is first-party — no third-party trackers, IPs are hashed (SHA-256).
        </p>
      </div>

      {/* Digest preview modal */}
      {showPreview && (
        <div
          data-testid="digest-preview-modal"
          className="fixed inset-0 z-[60] bg-brand-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !digestBusy && setShowPreview(false)}
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[90vh] flex flex-col border border-brand-green/15 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-brand-green/15">
              <div>
                <p className="eyebrow">◦  Digest Preview</p>
                <h3 className="font-display text-xl text-brand-black mt-1">Last {periodDays} day{periodDays === 1 ? "" : "s"}</h3>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                disabled={digestBusy}
                className="text-brand-black/50 hover:text-brand-black text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-brand-cream/40 p-2">
              {previewLoading ? (
                <div className="text-center py-20 text-brand-black/55 text-sm">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> Building preview…
                </div>
              ) : (
                <iframe
                  title="Digest preview"
                  data-testid="digest-preview-frame"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full min-h-[60vh] border-0 bg-white"
                />
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-brand-green/15">
              <button
                onClick={() => setShowPreview(false)}
                disabled={digestBusy}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={sendDigestNow}
                disabled={digestBusy || previewLoading}
                data-testid="digest-send-from-preview"
                className="btn-primary disabled:opacity-60"
              >
                {digestBusy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Send to madhu@spiruva.com
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
